import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { riskLayersAtom } from '@app/risk-migration/atoms/riskLayers.atoms'
import { useRiskFilterState } from '@app/risk-migration/hooks/useRiskFilterState'
import {
  riskWorkspaceAtom,
  riskWorkspaceCurrentStepAtom,
  riskWorkspaceStepAtom,
} from '../../shared/state/riskWorkspace.atom'
import { Step } from '../../shared/types/riskWorkspace.types'
import { EMPTY_WORKSPACE, EMPTY_WORKSPACE_STEPS } from '../workspace.constants'
import { mapLayersToWorkspaceLayers } from '../workspace.utils'
import { useWorkspaceLayerOptions } from './useWorkspaceLayerOptions'
import { toast } from '@app/components/ui/Toast/toast'

// Pre-fills the create workspace form with the layers and regions currently on the risk map
export const usePrefillWorkspaceFromMap = () => {
  const mapLayers = useAtomValue(riskLayersAtom)
  const { regionMasks } = useRiskFilterState()
  const layerOptions = useWorkspaceLayerOptions()
  const setWorkspaceForm = useSetAtom(riskWorkspaceAtom)
  const setWorkspaceSteps = useSetAtom(riskWorkspaceStepAtom)
  const setCurrentStep = useSetAtom(riskWorkspaceCurrentStepAtom)

  const prefillWorkspace = useCallback(() => {
    const { layers, unresolved } = mapLayersToWorkspaceLayers(
      mapLayers,
      layerOptions.hazardLayers,
      layerOptions.elementLayers,
      layerOptions.contextualLayers,
      layerOptions.liveLayers,
    )

    if (unresolved.length) {
      toast({
        title: 'Unresolved layers',
        description: 'Unfortunately some layers could not be added to the workspace:',
        children: (
          <ul className="mt-1 list-disc pl-5">
            {unresolved.map((layer) => (
              <li key={layer.id}>{layer.display_name}</li>
            ))}
          </ul>
        ),
      })
    }

    // the form holds a single area type, so only regions from the first region's group are kept
    const areaType = regionMasks?.[0]?.group_name
    const areas = areaType
      ? {
          area_type: areaType,
          areas: regionMasks
            .filter((region) => region.group_name === areaType)
            .map((region) => region.region),
        }
      : EMPTY_WORKSPACE.areas

    const droppedGroups = [
      ...new Set(
        (regionMasks ?? [])
          .filter((region) => region.group_name !== areaType)
          .map((region) => region.group_name),
      ),
    ]
    if (droppedGroups.length) {
      toast({
        title: 'Some regions not added',
        description: `Workspaces only support one region group. Only ${areaType} regions have been added; ${droppedGroups.join(', ')} regions were skipped.`,
      })
    }

    setWorkspaceForm({
      ...EMPTY_WORKSPACE,
      ...layers,
      name: 'New workspace',
      description: `Created from the risk map on ${new Date().toLocaleDateString()}`,
      areas,
    })
    setWorkspaceSteps(
      Object.fromEntries(Object.keys(EMPTY_WORKSPACE_STEPS).map((step) => [step, true])) as Record<
        Step,
        boolean
      >,
    )
    setCurrentStep('overview')
  }, [mapLayers, regionMasks, layerOptions, setWorkspaceForm, setWorkspaceSteps, setCurrentStep])

  return { prefillWorkspace }
}
